<script setup>
import { Icon } from '@iconify/vue'
import { ref, onMounted } from 'vue'

const currentSlide = ref(0)
const totalSlides = 3
const isAnimating = ref(false)

const touchStartX = ref(0)
const touchStartY = ref(0)
const touchEndX = ref(0)
const touchEndY = ref(0)
const minSwipeDistance = 50
const reverse = ref(false)
const nextSlide = () => {
  if (isAnimating.value) return
  reverse.value = false

  isAnimating.value = true
  currentSlide.value = (currentSlide.value + 1) % totalSlides
  setTimeout(() => {
    isAnimating.value = false
  }, 500)
}

const prevSlide = () => {
  if (isAnimating.value) return
  reverse.value = true
  isAnimating.value = true
  currentSlide.value = currentSlide.value === 0 ? totalSlides - 1 : currentSlide.value - 1
  setTimeout(() => {
    isAnimating.value = false
  }, 500)
}

const normalizeSlideIndex = (index) => {
  // Compatibility with nav indexes used elsewhere in the app:
  // About -> goToSlide(3), Contact -> goToSlide(4)
  if (totalSlides === 3 && index === 3) return 1
  if (totalSlides === 3 && index === 4) return 2
  return index
}

const goToSlide = (index) => {
  const normalizedIndex = normalizeSlideIndex(index)
  if (normalizedIndex < 0 || normalizedIndex >= totalSlides) return
  if (isAnimating.value || normalizedIndex === currentSlide.value) return

  reverse.value = normalizedIndex < currentSlide.value ? true : false;
  isAnimating.value = true
  currentSlide.value = normalizedIndex
  setTimeout(() => {
    isAnimating.value = false
  }, 500)
}

const handleTouchStart = (e) => {
  touchStartX.value = e.touches[0].clientX
  touchStartY.value = e.touches[0].clientY
}

const handleTouchMove = (e) => {
  if (!touchStartX.value || !touchStartY.value) return

  touchEndX.value = e.touches[0].clientX
  touchEndY.value = e.touches[0].clientY

  const deltaX = Math.abs(touchEndX.value - touchStartX.value)
  const deltaY = Math.abs(touchEndY.value - touchStartY.value)

  if (deltaX > deltaY) {
    e.preventDefault()
  }
}

const handleTouchEnd = () => {
  if (!touchStartX.value || !touchEndX.value) return

  const deltaX = touchStartX.value - touchEndX.value
  const deltaY = Math.abs(touchStartY.value - touchEndY.value)

  if (Math.abs(deltaX) > minSwipeDistance && Math.abs(deltaX) > deltaY) {
    deltaX > 0 ? nextSlide() : prevSlide()
  }

  touchStartX.value = 0
  touchStartY.value = 0
  touchEndX.value = 0
  touchEndY.value = 0
}

const handleMouseDown = (e) => {
  touchStartX.value = e.clientX
  const el = document.getElementById('sliderContainer')
  if (el) el.style.cursor = 'grabbing'
}

const handleMouseMove = (e) => {
  if (!touchStartX.value) return
  touchEndX.value = e.clientX
}

const handleMouseUp = () => {
  const el = document.getElementById('sliderContainer')

  if (!touchStartX.value || !touchEndX.value) {
    if (el) el.style.cursor = 'grab'
    return
  }

  const deltaX = touchStartX.value - touchEndX.value

  if (Math.abs(deltaX) > minSwipeDistance) {
    deltaX > 0 ? nextSlide() : prevSlide()
  }

  touchStartX.value = 0
  touchEndX.value = 0
  if (el) el.style.cursor = 'grab'
}

const handleKeyDown = (e) => {
  if (e.key === 'ArrowLeft') prevSlide()
  if (e.key === 'ArrowRight') nextSlide()
}

const setupEventListeners = () => {
  const el = document.getElementById('sliderContainer')
  if (!el) return

  el.addEventListener('touchstart', handleTouchStart, { passive: false })
  el.addEventListener('touchmove', handleTouchMove, { passive: false })
  el.addEventListener('touchend', handleTouchEnd, { passive: false })
  el.addEventListener('mousedown', handleMouseDown)
  el.addEventListener('mousemove', handleMouseMove)
  el.addEventListener('mouseup', handleMouseUp)
  el.addEventListener('mouseleave', handleMouseUp)

  document.addEventListener('keydown', handleKeyDown)
}


onMounted(() => {
  setupEventListeners()
})

// eslint-disable-next-line no-undef
defineExpose({
  goToSlide,
})


</script>
<template>
  <div class="relative h-screen max-h-[720px] bg-black overflow-hidden cursor-grab active:cursor-grabbing select-none" id="sliderContainer"
    @contextmenu.prevent>

    <Transition name="slide-fade">
      <div v-if="currentSlide === 0" :class="reverse ? 'reverse' : ''"
        class="min-w-full h-full flex flex-col justify-center items-center relative p-4 sm:p-8 bg-cover bg-center text-white text-center overflow-hidden hero-slide absolute inset-0">
        <div class="hero-background absolute inset-0 overflow-hidden z-0">
          <div class="floating-element"></div>
          <div class="floating-element"></div>
          <div class="floating-element"></div>
        </div>
        <div class="lampbox absolute z-20 w-full flex items-start justify-center h-full opacity-90 filter-blur-sm">
          <div class="lamp z-20 h-auto w-40 -mt-8 md:-mt-20 md:w-48 ">
            <img src="../assets/Asset 2.png" class="h-full w-full object-contain animate__animated animate__slideInDown" style="animation-delay: 500ms;" alt="">
          </div>
          <div class="lamp z-20 h-auto w-24 mt-0 md:-mt-20 md:w-32 ">
            <img src="../assets/Asset 2.png" class="h-full w-full object-contain animate__animated animate__slideInDown" alt="">
          </div>
          <div class="lamp z-20 h-auto w-40 -mt-8 md:-mt-20 md:w-48 ">
            <img src="../assets/Asset 2.png" class="h-full w-full object-contain animate__animated animate__slideInDown" style="animation-delay: 500ms;" alt="">
          </div>
        </div>

        <div
          class="hero-overlay absolute inset-0 z-10 pointer-events-none bg-gradient-radial from-black/70 via-black/25 to-transparent">
        </div>

        <div class="relative mt-40 md:mt-0 z-20 max-w-4xl mx-auto w-full">
          <div
            class="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 mx-auto mb-8 bg-gradient-to-br from-gray-800 to-gray-600 rounded-full flex items-center justify-center shadow-2xl animate-float">
            <svg class="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 fill-white" viewBox="0 0 24 24">
              <path
                d="M7 14c-1.66 0-3 1.34-3 3 0 1.31.84 2.41 2 2.83V21h2v-1.17c1.16-.42 2-1.52 2-2.83 0-1.66-1.34-3-3-3zM20.5 10H19V8.5c0-1.1-.9-2-2-2s-2 .9-2 2V10h-1.5c-.83 0-1.5.67-1.5 1.5v4c0 .83.67 1.5 1.5 1.5h5c.83 0 1.5-.67 1.5-1.5v-4c0-.83-.67-1.5-1.5-1.5zM16 8.5c0-.28.22-.5.5-.5s.5.22.5.5V10h-1V8.5z" />
            </svg>
          </div>

          <h1 class="text-4xl sm:text-5xl lg:text-7xl font-extrabold mb-4 leading-tight opacity-0 animate-fade-in-up-1">
            Selamat Datang di<br>
            <span class="text-yellow-500">ZUCOFFE</span>
          </h1>

          <p
            class="text-xs sm:text-base text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto opacity-0 animate-fade-in-up-2">
            Jl. Anggrek No. 27, Kel. Sukamaju, Kec. Cisayong
            Kota Fiktif, Jawa Barat 41234
          </p>

          <div class="flex gap-4 justify-center items-center opacity-0 animate-fade-in-up-3">
         
            <a href="#menu"
              class="w-auto py-2 pe-4 ps-2 rounded-full hover:shadow-md text-black hover:-translate-y-2 shadow-amber-500/40 duration-500 bg-amber-400 font-medium flex items-center gap-2"
              >
              <Icon icon="ion:cafe" class="text-black size-6" />
              <p>
                Lihat Menu
              </p>
            </a>
          </div>
        </div>
      </div>
    </Transition>
<Transition name="slide-fade">
  <div v-if="currentSlide === 1" :class="reverse ? 'reverse' : ''"
    class="min-w-full h-full flex flex-col justify-center items-center relative p-4 sm:p-8 bg-gradient-to-br from-black via-gray-950 to-black text-gray-100 overflow-hidden absolute inset-0">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 max-w-6xl w-full items-center">
      <div class="text-center lg:text-left">
        <h2 class="text-2xl sm:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-6 text-gray-50 leading-tight">
          Tentang <span class="text-amber-500">ZUCOFFE</span>
        </h2>
        <p class="text-sm sm:text-lg lg:text-xl leading-relaxed text-gray-300 mb-6 sm:mb-8">
          ZUCOFFE hadir untuk memberikan pengalaman kuliner terbaik dengan cita rasa autentik dan pelayanan
          **premium**. Kami menggunakan bahan-bahan berkualitas tinggi dan resep yang telah disempurnakan untuk
          menciptakan suasana cafe modern yang tak terlupakan.
        </p>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
          <div
            class="p-3 sm:p-6 bg-gray-900 rounded-xl shadow-md border border-gray-700 text-center hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
            <div class="text-xl sm:text-3xl lg:text-4xl font-extrabold text-amber-400 mb-1 sm:mb-2">5+</div>
            <div class="text-gray-400 font-medium text-xs sm:text-base">Tahun Pengalaman</div>
          </div>
          <div
            class="p-3 sm:p-6 bg-gray-900 rounded-xl shadow-md border border-gray-700 text-center hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
            <div class="text-xl sm:text-3xl lg:text-4xl font-extrabold text-amber-400 mb-1 sm:mb-2">1000+</div>
            <div class="text-gray-400 font-medium text-xs sm:text-base">Pelanggan Puas</div>
          </div>
          <div
            class="p-3 sm:p-6 bg-gray-900 rounded-xl shadow-md border border-gray-700 text-center hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
            <div class="text-xl sm:text-3xl lg:text-4xl font-extrabold text-amber-400 mb-1 sm:mb-2">50+</div>
            <div class="text-gray-400 font-medium text-xs sm:text-base">Menu Pilihan</div>
          </div>
        </div>
      </div>

      <div
        class="hidden lg:flex bg-gradient-to-br from-amber-950 to-amber-800 rounded-3xl h-72 lg:h-96 items-center justify-center shadow-2xl relative overflow-hidden mt-8 lg:mt-0">
        <div class="absolute inset-0 bg-contain bg-center bg-no-repeat opacity-5"
          style="background-image: url('https://www.transparentpng.com/thumb/restaurant/restaurant-menu-flat-design-concept-png-25.png');">
        </div>
        <div
          class="about-visual-bg absolute -top-1/2 -left-1/2 w-[200%] h-[200%] bg-gradient-radial from-white/5 to-transparent animate-spin-slow">
        </div>
        <svg class="w-24 h-24 lg:w-36 lg:h-36 fill-white opacity-70 relative z-10" viewBox="0 0 24 24">
          <path
            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
        </svg>
      </div>
    </div>
  </div>
</Transition>

<Transition name="slide-fade">
  <div v-if="currentSlide === 2" :class="reverse ? 'reverse' : ''"
    class="min-w-full h-full flex flex-col justify-center items-center relative px-4 py-4 sm:py-10 bg-gradient-to-br from-black via-gray-950 to-black text-white overflow-hidden absolute inset-0">
    <div class="absolute inset-0 opacity-10">
      <div class="absolute inset-0 float-animation"
        style="background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0); background-size: 40px 40px;">
      </div>
    </div>

    <div class="absolute top-1/4 left-1/4 w-20 h-20 border border-amber-700/20 rounded-full animate-spin"
      style="animation-duration: 15s;"></div>
    <div
      class="absolute bottom-1/3 right-1/4 w-16 h-16 bg-gradient-to-br from-amber-800/10 to-pink-800/10 rotate-45 animate-pulse">
    </div>
    <div class="absolute top-1/3 right-1/3 w-12 h-12 border-l-2 border-emerald-700/30 rounded-full animate-bounce"
      style="animation-duration: 3s;"></div>

    <div class="max-w-lg text-center w-full relative z-10">
      <div class="mb-6">
        <h2
          class="text-2xl sm:text-3xl font-bold mb-2 bg-gradient-to-r from-amber-600 via-amber-600 to-pink-600 bg-clip-text text-transparent">
          Hubungi Kami
        </h2>
        <p class="text-sm sm:text-base text-gray-500 leading-relaxed max-w-sm mx-auto">
          Kunjungi lokasi strategis kami untuk pengalaman kuliner terbaik
        </p>
      </div>

      <div class="grid grid-cols-2 gap-3 mb-6">
        <div
          class="group bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-4 hover:border-amber-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-700/10 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-2">
            <div class="p-2 bg-amber-700/20 rounded-lg group-hover:bg-amber-700/30 transition-colors">
              <svg class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
            </div>
            <span class="text-sm font-medium text-amber-600">Lokasi</span>
          </div>
          <p class="text-sm text-gray-400 text-left">Jl. Kuliner Raya No. 123<br />Jakarta Selatan</p>
        </div>

        <div
          class="group bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-4 hover:border-emerald-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-emerald-700/10 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-2">
            <div class="p-2 bg-emerald-700/20 rounded-lg group-hover:bg-emerald-700/30 transition-colors">
              <svg class="w-4 h-4 text-emerald-600" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
              </svg>
            </div>
            <span class="text-sm font-medium text-emerald-600">Telepon</span>
          </div>
          <p class="text-sm text-gray-400 text-left">+62 123 456 7890<br />Siap melayani Anda</p>
        </div>

        <div
          class="group bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-4 hover:border-amber-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-amber-700/10 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-2">
            <div class="p-2 bg-amber-700/20 rounded-lg group-hover:bg-amber-700/30 transition-colors">
              <svg class="w-4 h-4 text-amber-600" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
              </svg>
            </div>
            <span class="text-sm font-medium text-amber-600">Jam Buka</span>
          </div>
          <p class="text-sm text-gray-400 text-left">10:00 - 22:00<br />Setiap hari</p>
        </div>

        <div
          class="group bg-gradient-to-br from-gray-900/50 to-gray-950/50 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-4 hover:border-pink-700/50 transition-all duration-300 hover:shadow-lg hover:shadow-pink-700/10 hover:-translate-y-1">
          <div class="flex items-center gap-3 mb-2">
            <div class="p-2 bg-pink-700/20 rounded-lg group-hover:bg-pink-700/30 transition-colors">
              <svg class="w-4 h-4 text-pink-600" fill="currentColor" viewBox="0 0 24 24">
                <path
                  d="M12 2.04c-5.5 0-9.96 4.46-9.96 9.96 0 4.41 2.85 8.14 6.8 9.47.5.09.68-.22.68-.48 0-.24-.01-.87-.01-1.71-2.78.6-3.37-1.34-3.37-1.34-.45-1.15-1.11-1.46-1.11-1.46-.91-.62.07-.61.07-.61 1 .07 1.53 1.03 1.53 1.03.89 1.52 2.34 1.08 2.91.83.09-.65.35-1.08.63-1.33-2.22-.25-4.55-1.11-4.55-4.94 0-1.09.39-1.98 1.03-2.68-.1-.25-.45-1.27.1-2.65 0 0 .84-.27 2.75 1.02.8-.22 1.65-.33 2.5-.33.85 0 1.7.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.38.2 2.4.1 2.65.64.7 1.03 1.59 1.03 2.68 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85 0 1.33-.01 2.41-.01 2.73 0 .27.18.58.69.48 3.95-1.32 6.8-5.05 6.8-9.46 0-5.5-4.46-9.96-9.96-9.96z" />
              </svg>
            </div>
            <span class="text-sm font-medium text-pink-600">Follow Us</span>
          </div>
          <p class="text-sm text-gray-400 text-left">@cafekita<br />Instagram & TikTok</p>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-2 justify-center">
        <button
          class="group relative px-6 py-2.5 bg-gradient-to-r from-amber-800 to-amber-800 rounded-full text-sm font-medium overflow-hidden hover:shadow-lg hover:shadow-amber-700/25 transition-all duration-300 hover:-translate-y-0.5">
          <div
            class="absolute inset-0 bg-gradient-to-r from-amber-700 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          </div>
          <span class="relative flex items-center justify-center gap-2">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
            </svg>
            Google Maps
          </span>
        </button>

        <button
          class="group relative px-6 py-2.5 bg-gray-900/50 border border-gray-800/50 rounded-full text-sm font-medium hover:bg-gray-800/50 hover:border-gray-700/50 transition-all duration-300 hover:-translate-y-0.5">
          <span class="flex items-center justify-center gap-2">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
              <path
                d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
            </svg>
            Hubungi
          </span>
        </button>
      </div>
    </div>
  </div>
</Transition>
    


    <button
      class="absolute top-1/2 left-4 lg:left-8 -translate-y-1/2 w-12 h-12 lg:w-16 lg:h-16 bg-black/40 shadow-md shadow-amber-400/30 border border-white/20 text-amber-400 rounded-full flex items-center justify-center transition-all duration-300 z-20 hidden sm:flex"
      @click="prevSlide">
      <Icon icon="icon-park-outline:left" class="size-6 md:size-8" />
    </button>

    <button
      class="absolute top-1/2 right-4 lg:right-8 -translate-y-1/2 w-12 h-12 lg:w-16 lg:h-16 bg-black/40 shadow-md shadow-amber-400/30 border border-white/20 text-amber-400 rounded-full flex items-center justify-center transition-all duration-300 z-20 hidden sm:flex"
      @click="nextSlide">
      <Icon icon="icon-park-outline:right" class="size-6 md:size-8" />
    </button>

    <div class="absolute bottom-6 lg:bottom-8 left-1/2 -translate-x-1/2 flex gap-4 z-40">
      <div class="w-3 h-3 rounded-full cursor-pointer transition-all duration-300"
        :class="currentSlide === 0 ? 'bg-gray-600 scale-125' : 'bg-white/50'" @click="goToSlide(0)"></div>
      <div class="w-3 h-3 rounded-full cursor-pointer transition-all duration-300"
        :class="currentSlide === 1 ? 'bg-gray-600 scale-125' : 'bg-white/50'" @click="goToSlide(1)"></div>
      <div class="w-3 h-3 rounded-full cursor-pointer transition-all duration-300"
        :class="currentSlide === 2 ? 'bg-gray-600 scale-125' : 'bg-white/50'" @click="goToSlide(2)"></div>
    </div>
  </div>
</template>



<style>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.5s ease;
  position: absolute; 
  width: 100%;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(100%); 
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-100%); 
}


.hero-slide {
  background: url('../assets/bg-home.jpg');
  background-size: cover;
  background-position: center;
}

.hero-slide::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  pointer-events: none;
   background:
    linear-gradient(to bottom, rgb(0, 0, 0) 0%, transparent 60%),
    linear-gradient(to top, rgb(0, 0, 0) 1%, transparent 100%);
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.05;
  animation: float 8s ease-in-out infinite;
}

.floating-element:nth-child(1) {
  top: 10%;
  left: 10%;
  width: clamp(150px, 25vw, 250px);
  height: clamp(150px, 25vw, 250px);
  background: #ffffff;
  animation-delay: 0s;
}

.floating-element:nth-child(2) {
  top: 60%;
  right: 10%;
  width: clamp(200px, 30vw, 350px);
  height: clamp(200px, 30vw, 350px);
  background: #6b7280;
  animation-delay: 2s;
}

.floating-element:nth-child(3) {
  bottom: 10%;
  left: 50%;
  width: clamp(180px, 28vw, 300px);
  height: clamp(180px, 28vw, 300px);
  background: #9ca3af;
  animation-delay: 4s;
}

/* Custom animations that can't be done with Tailwind */
@keyframes bgDrift {
  0% {
    background-size: 100%;
  }

  50% {
    background-size: 105%;
  }

  100% {
    background-size: 100%;
  }
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }

  50% {
    transform: translateY(-20px) rotate(180deg);
  }
}

.animate-float {
  animation: logoFloat 3s ease-in-out infinite;
}

@keyframes logoFloat {

  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-10px);
  }
}

.animate-spin-slow {
  animation: rotate 20s linear infinite;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Staggered fade in animations */
.animate-fade-in-up-1 {
  animation: fadeInUp 1s ease-out 0.5s forwards;
}

@keyframes float {

  0%,
  100% {
    transform: translateY(0px) rotate(0deg);
  }

  50% {
    transform: translateY(-10px) rotate(180deg);
  }
}

.animate-fade-in-up-2 {
  animation: fadeInUp 1s ease-out 0.8s forwards;
}

.animate-fade-in-up-3 {
  animation: fadeInUp 1s ease-out 1.1s forwards;
}
.slide-fade-enter-active.reverse,
.slide-fade-leave-active.reverse {
  transition: all 0.5s ease;
  position: absolute;
  width: 100%;
}

.slide-fade-enter-from.reverse {
  opacity: 0;
  transform: translateX(-100%);
}

.slide-fade-leave-to.reverse {
  opacity: 0;
  transform: translateX(100%);
}
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {

  .hero-slide,
  .floating-element,
  .animate-float,
  .animate-spin-slow,
  .animate-fade-in-up-1,
  .animate-fade-in-up-2,
  .animate-fade-in-up-3 {
    animation: none !important;
  }

  .animate-fade-in-up-1,
  .animate-fade-in-up-2,
  .animate-fade-in-up-3 {
    opacity: 1 !important;
  }
}
</style>